import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Space,
  Row,
  Col,
  message,
  Alert,
  Popover,
  Table,
  Tooltip,
  Radio,
  Empty,
  Modal as AntdModal,
} from "antd";
import { Spinner } from "reactstrap";
import { Option } from "antd/es/mentions";
import {
  PaperClipOutlined,
  DownloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  DeleteOutlined,
  UploadOutlined,
  PlusSquareOutlined,
  EditOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import SiteInventoryAllocationApprovalsDrawer from "./SiteInventoryAllocationApprovalsDrawer";
import usersApis from "../../api/master/users";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import { useDispatch, useSelector } from "react-redux";
import { fetchSequenceAccess } from "../../store/slices/misc/sequenceAccess";
import contractorsApis from "../../api/master/contractors";
import sequenceApis from "../../api/master/sequence";
import rolesApi from "../../api/master/roles";
import inventoryCreation from "../../api/inventory/inventoryCreation";
import Swal from "sweetalert2";


// Expandable panel for a single material line. Renders the task allocations
// in a nested antd Table so the child level reads as clearly distinct from
// the parent material row.
const LineTaskPanel: React.FC<any> = ({
  tasks,
  taskOptions,
  onAddTask,
  onUpdateTask,
  onRemoveTask,
}) => {
  const taskColumns = [
    {
      title: "#",
      key: "index",
      width: 44,
      align: "center" as const,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Task",
      key: "task",
      width: 200,
      render: (_: any, row: any) => (
        <Select
          placeholder="Select Task"
          showSearch
          optionFilterProp="children"
          allowClear
          style={{ width: "100%" }}
          value={row.taskId}
          onChange={(val) => {
            const next = taskOptions.find((t: any) => t.taskId === val);
            onUpdateTask(row.key, {
              taskId: val ?? undefined,
              taskBoqId: next?.taskBoqId ?? undefined,
            });
          }}
        >
          {taskOptions.map((t: any) => (
            <Option key={t.taskId} value={t.taskId}>
              {t.taskName}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "BOQ Line",
      key: "boqLine",
      width: 140,
      ellipsis: { showTitle: true } as any,
      render: (_: any, row: any) => {
        const picked = taskOptions.find((t: any) => t.taskId === row.taskId);
        return picked?.boqLineName ?? "--";
      },
    },
    {
      title: "Site Structure",
      key: "siteStructure",
      width: 140,
      ellipsis: { showTitle: true } as any,
      render: (_: any, row: any) => {
        const picked = taskOptions.find((t: any) => t.taskId === row.taskId);
        return picked?.siteStructureUnitName ?? "--";
      },
    },
    {
      title: "Allocated Qty",
      key: "allocatedQty",
      width: 110,
      align: "right" as const,
      render: (_: any, row: any) => {
        const picked = taskOptions.find((t: any) => t.taskId === row.taskId);
        return picked?.taskAllocatedQty ?? "--";
      },
    },
    {
      title: "Issued Qty",
      key: "issuedQty",
      width: 100,
      align: "right" as const,
      render: (_: any, row: any) => {
        const picked = taskOptions.find((t: any) => t.taskId === row.taskId);
        return picked?.issuedQuantity ?? "--";
      },
    },
    {
      title: "Qty",
      key: "qty",
      width: 110,
      render: (_: any, row: any) => (
        <Input
          type="number"
          min={0}
          step={0.001}
          placeholder="Qty"
          value={row.uomQty}
          onChange={(e) => onUpdateTask(row.key, { uomQty: e.target.value })}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 48,
      align: "center" as const,
      render: (_: any, row: any) => (
        <Tooltip title="Remove task">
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => onRemoveTask(row.key)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div
      style={{
        background: "#f5f8fb",
        borderLeft: "3px solid #1890ff",
        borderRadius: 4,
        padding: "12px 16px",
        margin: "4px 0",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <span style={{ fontWeight: 600, color: "#262626", fontSize: 14 }}>
            Task-wise Allocation
          </span>
          <span
            style={{
              color: "#8c8c8c",
              fontSize: 12,
              marginLeft: 8,
            }}
          >
            ({tasks.length} {tasks.length === 1 ? "task" : "tasks"})
          </span>
        </div>
        <Button
          type="primary"
          ghost
          icon={<PlusCircleOutlined />}
          onClick={onAddTask}
        >
          Add Task
        </Button>
      </div>

      {tasks.length === 0 ? (
        <div
          style={{
            background: "#fff",
            border: "1px dashed #d9d9d9",
            borderRadius: 4,
            padding: "24px 16px",
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: "#8c8c8c" }}>
                No tasks added. Click <b>Add Task</b> to allocate this material.
              </span>
            }
          />
        </div>
      ) : (
        <Table
          columns={taskColumns}
          dataSource={tasks}
          rowKey="key"
          pagination={false}
          size="small"
          bordered
          tableLayout="fixed"
          style={{ background: "#fff", borderRadius: 4 }}
        />
      )}
    </div>
  );
};

const SiteInventoryAllocationDrawer: React.FC<any> = ({
  visible,
  onClose,
  site,
  contractor,
  lines,
  onSubmit,
  exceededMaterials = [],
}) => {
  const dispatch: any = useDispatch();
  const { data: sequenceAccess } = useSelector(
    (state: any) => state.sequenceAccess
  );
  console.log("Site::", site);
  const [sitesOptions, setSitesOptions] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [approvalRequired, setApprovalRequired] = useState("false");
  // Available BOQ tasks per material line, keyed by materialId. Fetched on
  // demand via /task/tasksByMaterial when a row is expanded.
  const [taskOptionsByMaterial, setTaskOptionsByMaterial] = useState<
    Record<string, any[]>
  >({});
  // Per material line task allocations, keyed by the line's paginationId.
  // Each entry: { key, taskId, taskBoqId, uomQty }.
  const [lineTasks, setLineTasks] = useState<Record<string, any[]>>({});

  const [isFinalApprover, setIsFinalApprover] = useState<boolean>(false);
  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<'auto' | 'manual'>('auto');

  const makeTaskKey = () =>
    `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const addTask = (lineKey: string) =>
    setLineTasks((prev) => ({
      ...prev,
      [lineKey]: [
        ...(prev[lineKey] || []),
        {
          key: makeTaskKey(),
          taskId: undefined,
          taskBoqId: undefined,
          uomQty: undefined,
        },
      ],
    }));

  const updateTask = (lineKey: string, taskKey: string, changes: any) =>
    setLineTasks((prev) => ({
      ...prev,
      [lineKey]: (prev[lineKey] || []).map((t) =>
        t.key === taskKey ? { ...t, ...changes } : t
      ),
    }));

  const removeTask = (lineKey: string, taskKey: string) =>
    setLineTasks((prev) => ({
      ...prev,
      [lineKey]: (prev[lineKey] || []).filter((t) => t.key !== taskKey),
    }));

  const handleFormSubmit = (values: any) => {
    const resolvedSiteValue =
      sitesOptions.find((item: any) => item.value == String(site))?.value ??
      (site ? site.toString() : undefined);

    const formDataToSubmit = { ...values };
    console.log(formDataToSubmit, "bfr");

    if (numberingMode === 'auto') {
      delete formDataToSubmit.issueId;
    }

    formDataToSubmit.siteId = resolvedSiteValue;
    // Ensure isChargeable is included in the form data
    formDataToSubmit.isChargeable = values.isChargeable;
    formDataToSubmit.isStrictApproval = isStrictApproval;
    formDataToSubmit.numberingMode = numberingMode;
    console.log("Form data on submit:", values);

    const hasApprovalsSet =
      Array.isArray(approvals?.data) && approvals.data.length > 0;
    const shouldBlockDueToBoq =
      exceededMaterials.length > 0 &&
      (approvalRequired !== "true" || !hasApprovalsSet) &&
      isFinalApprover !== true;

    if (shouldBlockDueToBoq) {
      const materialsHtml = exceededMaterials
        .map(
          (item: any) =>
            `<b>${item.label}</b>: Entered Qty (${item.enteredQty}) exceeds Issue Remaining Qty (${item.remainingQty})`
        )
        .join("<br/>");
      const extraMessage =
        approvalRequired === "true"
          ? " You need to set the approvals to proceed."
          : " Approval is required to proceed.";
      Swal.fire({
        icon: "warning",
        title: "Issue Quantity Exceeded",
        html: `${materialsHtml}<br/><br/>${extraMessage}`,
      });
      return;
    }

    // Quantity rules:
    //  - Each line's Qty To Issue must not exceed its Qty Available For Utilization.
    //  - When any task allocations are added for a line, the sum of those task
    //    qtys must equal the line's Qty To Issue (so it also cannot exceed it).
    const quantityViolations: string[] = [];
    (lines || []).forEach((line: any) => {
      const lineUomQty = Number(line.uomQty);
      const qtyAvailable = Number(line.quantityAvailable);
      const materialName =
        line.materialName || `Line ${line.paginationId ?? ""}`.trim();

      if (
        !Number.isNaN(lineUomQty) &&
        !Number.isNaN(qtyAvailable) &&
        lineUomQty > qtyAvailable
      ) {
        quantityViolations.push(
          `<b>${materialName}</b>: Qty To Issue (${lineUomQty}) exceeds Qty Available For Utilization (${qtyAvailable}).`
        );
      }

      const taskList = lineTasks[line.paginationId] || [];
      if (taskList.length > 0 && !Number.isNaN(lineUomQty)) {
        const taskSum = taskList.reduce(
          (acc: number, t: any) =>
            acc + (t?.uomQty != null && t.uomQty !== "" ? Number(t.uomQty) : 0),
          0
        );
        if (taskSum !== lineUomQty) {
          quantityViolations.push(
            `<b>${materialName}</b>: Task-wise total (${taskSum}) must equal Qty To Issue (${lineUomQty}).`
          );
        }
      }
    });

    if (quantityViolations.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Quantity Validation",
        html: quantityViolations.map((m) => `• ${m}`).join("<br/>"),
      });
      return;
    }

    setDisableSubmit(true);
    onSubmit({
      FormData: formDataToSubmit,
      approvalsData: approvals?.data,
      lineTasks,
    });
    setDisableSubmit(false);
  };
  useEffect(() => {
    fetchSites();
  }, [site]);

  useEffect(() => {
    if (contractor) {
      form.setFieldsValue({ contractorId: contractor.toString() });
    }
  }, [contractor, contractorOptions, form]);

  useEffect(() => {
    if (!site) {
      setIsFinalApprover(false);
      return;
    }
    (async () => {
      try {
        const moduleRes = await rolesApi.findAllModuleConfig();
        const moduleID = moduleRes?.data?.find(
          (x: any) => x.moduleName == "Material Issue"
        )?.moduleID;
        let isFinal = false;
        if (moduleID) {
          const res = await rolesApi.fetchHierarchyUsersByModuleId(
            moduleID,
            site
          );
          const msg = (res?.message || "").toString().toLowerCase();
          isFinal = msg.includes("you are the final");
        }
        setIsFinalApprover(isFinal);
      } catch (err) {
        console.log("Error checking final approver:", err);
        setIsFinalApprover(false);
      }
    })();
  }, [site]);

  const fetchTasksForMaterial = useCallback(
    async (materialId: any) => {
      if (!site || !materialId || !contractor) return;
      const key = String(materialId);
      try {
        const res: any = await inventoryCreation.fetchTasksByMaterial({
          siteId: site,
          materialId,
          contractorId: contractor,
        });
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.response)
            ? res.response
            : [];
        setTaskOptionsByMaterial((prev) => ({ ...prev, [key]: list }));
      } catch (error) {
        console.log("Error fetching tasks by material:", error);
        setTaskOptionsByMaterial((prev) => ({ ...prev, [key]: [] }));
      }
    },
    [site, contractor]
  );

  useEffect(() => {
    form.setFieldsValue({
      approvals: "false",
      isChargeable: "false",
    });
    setApprovalRequired("false");
    setLineTasks({});
    setTaskOptionsByMaterial({});
  }, [site, contractor, form]);

  useEffect(() => {
    dispatch(fetchSequenceAccess());
  }, [dispatch]);

  const columns = [
    {
      title: "#",
      key: "#",
      width: 50,
      render: (text: any, record: any, index: any) => index + 1,
    },
    {
      title: "Site",
      dataIndex: "siteName",
      key: "siteName",
      width: 200,
    },
    {
      title: "Material Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 200,
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 200,
    },
    {
      title: "UOM",
      dataIndex: "primaryUomName",
      key: "primaryUomName",
      width: 200,
    },
    {
      title: "Qty Available For Utilization",
      dataIndex: "quantityAvailable",
      key: "quantityAvailable",
      width: 220,
      render: (text: number, record: any) => (
        <div>
          <span>{text?.toLocaleString("en-IN")}</span>
        </div>
      ),
    },
    {
      title: "Qty To Issue",
      dataIndex: "uomQty",
      key: "uomQty",
      editable: true,
      width: 170,
    },
  ];

  const fetchContractorList = async (siteId: any) => {
    try {
      if (!siteId) return;
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: siteId + "",
      });
      if (status) {
        let allContractors: any = [];
        let _contractorOptions: any = [];
        data.forEach((item: any) => {
          allContractors.push(item?.contractorID?.toString());
          _contractorOptions.push({
            label: item?.companyName,
            value: item?.contractorID?.toString(),
          });
        });
        if (_contractorOptions.length > 1) {
          _contractorOptions = [..._contractorOptions];
        }
        setContractorOptions(_contractorOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        let sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item?.siteID?.toString(),
        }));
        console.log("sitesOptions", sitesOptions);
        setSitesOptions(sitesOptions);
        const selectedSite = sitesOptions.find(
          (item: any) => item.value === String(site)
        );
        form.setFieldValue("siteId", selectedSite?.label || "");
        fetchContractorList(site);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterialIssueSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Material Issue" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      setActiveSequences(_sequenceAccess || []);

      // Set initial numbering mode based on available sequences
      if (_sequenceAccess && _sequenceAccess.length > 0 && site) {
        const siteHasSequence = _sequenceAccess.some(
          (seq: any) => seq.siteId?.toString() === site?.toString()
        );
        setNumberingMode(siteHasSequence ? 'auto' : 'manual');
      } else {
        setNumberingMode('manual');
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setActiveSequences([]);
      setNumberingMode('manual');
    }
  };

  useEffect(() => {
    if (site) {
      fetchMaterialIssueSequenceAccess();
    } else {
      setActiveSequences([]);
    }
  }, [site]);

  const getCurrentSiteSequence = useCallback(() => {
    if (!site || activeSequences.length === 0) return null;

    const siteSequence = activeSequences.find(
      (seq) =>
        seq.level === "Site" &&
        seq.siteId?.toString() === site?.toString()
    );

    if (siteSequence) return siteSequence;

    const orgSequence = activeSequences.find(
      (seq) => seq.level === "Org"
    );

    if (orgSequence) return orgSequence;

    return null;
  }, [site, activeSequences]);

  const currentSiteSequence = getCurrentSiteSequence();
  const canAuto = !!site && !!currentSiteSequence;

  // Auto-generate number when in auto mode 
  useEffect(() => {
    const existingValue = form.getFieldValue("issueId");

    if (numberingMode === 'auto' && currentSiteSequence && !existingValue) {
      const { prefix } = currentSiteSequence;
      const issueNumber = `${prefix}`;
      form.setFieldValue('issueId', issueNumber);
    }
  }, [numberingMode, currentSiteSequence, form]);

  const prevSiteRef = useRef();

  // Set initial mode based on site selection 
  useEffect(() => {
    const currentSite = site;
    const prevSite = prevSiteRef.current;
    // Only run if site actually changed
    if (prevSite && prevSite !== currentSite) {
      form.setFieldValue("issueId", "");
    }
    if (!currentSite) {
      setNumberingMode("manual");
    } else {
      const siteSequence = getCurrentSiteSequence();
      setNumberingMode(siteSequence ? "auto" : "manual");
    }
    prevSiteRef.current = currentSite;
  }, [site]);

  return (
    <Drawer
      title="Material Issue Create"
      width={"80vw"}
      onClose={onClose}
      open={visible}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          {disableSubmit ? (
            <Button color="primary" type="primary">
              <Spinner size="sm" color="light" />
            </Button>
          ) : (
            <Button onClick={() => form.submit()} type="primary">
              Submit
            </Button>
          )}
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        <div className="justify-start w-100  flex-wrap pt-3">

          <div style={{ flex: "1 1 calc(33.333% - 12px)", minWidth: "650px", maxWidth: "calc(33.333% - 12px)" }} className="p-2 pt-0 pb-0">
            <div style={{ position: 'relative' }}>
              <Form.Item
                name="issueId"
                label={<div className="data-label w-100 mb-1">Issue Number</div>}
                rules={[
                  {
                    required: numberingMode === 'manual',
                    message: "Please Enter Issue Number",
                  },
                ]}
              >
                <Input
                  placeholder={numberingMode === 'auto' ? 'Auto-generated' : 'Enter Issue Number'}
                  className="w-100"
                  disabled={numberingMode === 'auto'}
                />
              </Form.Item>
              <Tooltip title="Click here to enable or disable auto-generation of issue numbers.">
                <SettingOutlined
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '60px',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#1890ff',
                    zIndex: 1,
                  }}
                  onClick={() => {
                    setTempNumberingMode(numberingMode);
                    setShowNumberingModal(true);
                  }}
                />
              </Tooltip>
            </div>
          </div>
          <Form.Item
            name="siteId"
            label={<div className="data-label w-100 mb-1">Site</div>}
            className="w-50 p-2 paddingLeft10 pt-0 pb-0"
          >
            <Input disabled type="text" placeholder="Site" className="w-100" />
          </Form.Item>
        </div>
        <div className="justify-start w-100 flex-wrap">
          <Form.Item
            name="contractorId"
            label={<div className="data-label w-90 mb-1">Contractor</div>}
            className="w-50 p-3 paddingLeft10  pt-0 pb-0"
            rules={[
              {
                required: true,
                message: "Please select",
              },
            ]}
          >
            <Select placeholder="Select Contractor" disabled={!!contractor}>
              {contractorOptions.map((item: any) => (
                <Option value={item.value}>{item.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="approvals"
            label={
              <div className="data-label w-100 mb-1">Approval Required ?</div>
            }
            className="w-50 p-3 paddingLeft10 pt-0 pb-0"
            rules={[
              {
                required: true,
                message: "Please select approvals",
              },
            ]}
          >
            <Select
              placeholder="Select Approvals"
              value={approvalRequired}
              onChange={(e) => {
                form.setFieldValue("approvals", e);
                setApprovalRequired(() => e);
              }}
            >
              <Option value="true">Yes</Option>
              <Option value="false">No</Option>
            </Select>
            {approvalRequired == "true" && (
              <Popover
                content="Click here to set required approvals"
                title="Approval Required"
              >
                <a
                  href="#"
                  className="success"
                  onClick={(e) => {
                    e.preventDefault();
                    setApprovals((pre) => {
                      return {
                        ...pre,
                        open: true,
                      };
                    });
                  }}
                >
                  <EditOutlined /> Set Approvals &nbsp;
                  {approvals?.data?.length == 0 ? (
                    <span className="mandatory">Please set approvals</span>
                  ) : (
                    <></>
                  )}
                </a>
              </Popover>
            )}
          </Form.Item>
        </div>
        {/* Common Is Chargeable field - always shows */}
        <div className="justify-start w-100 flex-wrap">
          <Form.Item
            name="isChargeable"
            label={<div className="data-label w-90 mb-1">Is Chargeable ?</div>}
            className="w-50 p-3 paddingLeft10  pt-0 pb-0"
            initialValue="false"
          >
            <Select placeholder="Select">
              <Option value="true">Yes</Option>
              <Option value="false">No</Option>
            </Select>
          </Form.Item>
        </div>
      </Form>

      <Row className="mt-3">
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">
                <span> Materials Issued</span>
              </BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <div>
          <div
            style={{
              color: "#8c8c8c",
              fontSize: 13,
              marginTop: 8,
            }}
          >
            Expand a material row to allocate it task-wise.
          </div>
          <div
            style={{
              // width: screenSize - 330 + "px",
              minHeight: "200px",
              marginTop: "20px",
            }}
          >
            <Table
              columns={columns}
              dataSource={lines}
              rowKey="paginationId"
              bordered
              size="middle"
              expandable={{
                expandedRowRender: (record: any) => (
                  <LineTaskPanel
                    tasks={lineTasks[record.paginationId] || []}
                    taskOptions={
                      taskOptionsByMaterial[String(record.materialId)] || []
                    }
                    onAddTask={() => addTask(record.paginationId)}
                    onUpdateTask={(taskKey: string, changes: any) =>
                      updateTask(record.paginationId, taskKey, changes)
                    }
                    onRemoveTask={(taskKey: string) =>
                      removeTask(record.paginationId, taskKey)
                    }
                  />
                ),
                rowExpandable: () => true,
                onExpand: (expanded: boolean, record: any) => {
                  if (
                    expanded &&
                    record?.materialId &&
                    !taskOptionsByMaterial[String(record.materialId)]
                  ) {
                    fetchTasksForMaterial(record.materialId);
                  }
                },
              }}
            />
          </div>
        </div>
      </Row>

      {approvals?.open && (
        <SiteInventoryAllocationApprovalsDrawer
          open={approvals.open}
          siteId={site}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status: boolean) => { }}
          approvalData={approvals.data} // Pass the current approval data
        />
      )}

      {/* Material Issue Number Configuration Modal */}
      <AntdModal
        title="Configure Material Issue Number Preference"
        open={showNumberingModal}
        onCancel={() => setShowNumberingModal(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px" }}>
            <Button
              key="save"
              color="primary"
              onClick={() => {
                if (tempNumberingMode === "auto" && !currentSiteSequence) {
                  message.error("Auto-generation is not configured for this site.");
                  return;
                }

                setNumberingMode(tempNumberingMode);

                if (tempNumberingMode === "auto" && currentSiteSequence) {
                  const { prefix } = currentSiteSequence;
                  const issueNumber = `${prefix}`;
                  form.setFieldValue("issueId", issueNumber);
                } else if (tempNumberingMode === "manual") {
                  form.setFieldValue("issueId", "");
                }
                setShowNumberingModal(false);
              }}
            >
              Save
            </Button>

            <Button
              key="cancel"
              color="primary"
              onClick={() => setShowNumberingModal(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 20, lineHeight: "22px" }}>
          Your material issue numbers are currently set to auto-generate to save time.
          <br />
          Are you sure you want to change this setting?
        </div>

        {!site && (
          <div style={{ color: "#8c8c8c", marginBottom: 16 }}>
            <InfoCircleOutlined /> Sequence configuration is not set for this site. Please configure the sequence.
          </div>
        )}

        {site && !currentSiteSequence && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined />{" "}
            {activeSequences.length === 0
              ? "No sequences configured for Material Issue. Please set up a sequence first."
              : "No sequence configured for the selected site. Please set a sequence for this site."}
          </div>
        )}

        <Radio.Group
          onChange={(e) => setTempNumberingMode(e.target.value)}
          value={tempNumberingMode}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Radio value="auto" disabled={!canAuto}>
              <span style={{ fontWeight: 500 }}>
                Continue auto-generating material issue numbers
              </span>
            </Radio>

            <Radio value="manual">
              <span style={{ fontWeight: 500 }}>
                Enter material issue numbers manually
              </span>
            </Radio>
          </Space>
        </Radio.Group>
      </AntdModal>
    </Drawer>
  );
};

export default SiteInventoryAllocationDrawer;
